import React, { useCallback, useEffect, useState, useRef } from 'react';
import {
  Card, Empty, Typography, Spin, Button, Avatar, Space, Tag, message, Modal,
  Drawer, Input,
} from 'antd';
import {
  MailOutlined, InboxOutlined, UserOutlined, CheckOutlined, CloseOutlined,
  StarOutlined, CalendarOutlined, MessageOutlined, SendOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const { Title, Text } = Typography;

const STATUS_COLOR = { PENDING: 'orange', ACCEPTED: 'green', REJECTED: 'default', CANCELLED: 'default' };
const STATUS_LABEL = { PENDING: 'Aguardando', ACCEPTED: 'Aceito', REJECTED: 'Recusado', CANCELLED: 'Cancelado' };

export default function TeamOwnerInvites() {
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [teamId, setTeamId] = useState(null);
  const [invites, setInvites] = useState([]);
  const [acting, setActing] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatInvite, setChatInvite] = useState(null);
  const [chatMsgs, setChatMsgs] = useState([]);
  const [chatDraft, setChatDraft] = useState('');
  const [chatSending, setChatSending] = useState(false);
  const chatBottomRef = useRef(null);

  const openChat = async (inv) => {
    setChatOpen(true); setChatInvite(inv); setChatDraft(''); setChatMsgs([]);
    try {
      const r = await api.get(`/community/invites/${inv.id}/messages`);
      setChatMsgs(r.data.messages || []);
      // marca como lida no widget (best-effort)
      api.post('/community/me/inbox/read', { kind: 'invite', ref_id: inv.id }).catch(() => {});
    } catch (e) { message.error('Erro ao carregar chat'); }
  };

  const sendChat = async () => {
    const body = chatDraft.trim();
    if (!body || !chatInvite) return;
    setChatSending(true);
    try {
      const r = await api.post(`/community/invites/${chatInvite.id}/messages`, { body });
      setChatMsgs(prev => [...prev, r.data.message || { id: Date.now(), body, sender_type: 'team', created_at: new Date().toISOString() }]);
      setChatDraft('');
      setTimeout(() => chatBottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    } catch (e) { message.error('Erro ao enviar'); }
    finally { setChatSending(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/community/team-owner/my');
      const t = r.data?.teams?.[0];
      if (!t) { setInvites([]); setTeamId(null); return; }
      setTeamId(t.id);
      const inv = await api.get(`/community/team-owner/${t.id}/invites`);
      setInvites(inv.data.invites || []);
    } catch (e) {
      message.error(e?.response?.data?.message || 'Erro ao carregar');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Deep link: abre chat do convite quando chega com ?invite_id=X ou quando
  // NotificationCenter dispara 'convites:open-drawer'. Reutiliza openChat.
  useEffect(() => {
    const openById = (id) => {
      const found = invites.find(x => String(x.id) === String(id));
      if (found) openChat(found);
    };
    const q = new URLSearchParams(window.location.search).get('invite_id');
    if (q && invites.length) {
      openById(q);
      // Limpa query pra nao reabrir em re-render
      const url = new URL(window.location.href);
      url.searchParams.delete('invite_id');
      window.history.replaceState(null, '', url.pathname + url.search);
    }
    const handler = (e) => openById(e.detail?.id);
    window.addEventListener('convites:open-drawer', handler);
    return () => window.removeEventListener('convites:open-drawer', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invites]);

  const respond = (inv, action) => {
    Modal.confirm({
      title: action === 'accept' ? 'Aceitar esse jogador?' : 'Recusar essa candidatura?',
      content: `${inv.player_name} vai ser notificado da decisão.`,
      okText: action === 'accept' ? 'Aceitar' : 'Recusar',
      okType: action === 'accept' ? 'primary' : 'danger',
      cancelText: 'Voltar',
      onOk: async () => {
        setActing(inv.id);
        try {
          await api.post(`/community/team-owner/invites/${inv.id}/respond`, { action });
          // Dispara refresh do sino/notificacoes — sem isso, o contador
          // pendente so cai apos F5.
          window.dispatchEvent(new CustomEvent('notifications:refresh'));
          message.success(action === 'accept' ? 'Aceito' : 'Recusado');
          load();
        } catch (e) {
          message.error(e?.response?.data?.message || 'Erro');
        } finally { setActing(null); }
      },
    });
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>;

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: 16 }}>
      <div style={{ marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}><MailOutlined /> Convites do time</Title>
        <Text type="secondary">Candidaturas de jogadores que querem entrar no teu time.</Text>
      </div>

      {invites.length === 0 ? (
        <Card>
          <div style={{ textAlign: 'center', padding: 20 }}>
            <InboxOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 12 }} />
            <div><Text type="secondary">Nenhuma candidatura ainda.</Text></div>
            {teamId && (
              <Button type="link" onClick={() => nav('/time/procurar')} style={{ marginTop: 8 }}>
                Procura jogadores perto do teu time →
              </Button>
            )}
          </div>
        </Card>
      ) : (
        invites.map(inv => (
          <Card key={inv.id} size="small" style={{ marginBottom: 10 }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <Avatar size={44} src={inv.profile_photo} icon={<UserOutlined />} />
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontWeight: 700, fontSize: 15 }}>
                  {inv.player_name}
                  <Tag color={STATUS_COLOR[inv.status] || 'default'} style={{ marginLeft: 8 }}>
                    {STATUS_LABEL[inv.status] || inv.status}
                  </Tag>
                </div>
                <Space size={6} wrap style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
                  {inv.preferred_position && <Tag>{inv.preferred_position}</Tag>}
                  {inv.skill_level && <Tag color="blue">{inv.skill_level}</Tag>}
                  {inv.player_total_ratings > 0 ? (
                    <Tag color="purple" title={`Média de ${inv.player_total_ratings} avaliações no Futezap`}>
                      <StarOutlined /> {parseFloat(inv.player_rating).toFixed(1)} Futezap
                    </Tag>
                  ) : inv.player_rating > 0 && (
                    <Tag color="purple"><StarOutlined /> {parseFloat(inv.player_rating).toFixed(1)}</Tag>
                  )}
                  {inv.player_total_games > 0 && <Tag>{inv.player_total_games} jogos</Tag>}
                  {inv.game_date && (
                    <span><CalendarOutlined /> {new Date(inv.game_date).toLocaleDateString('pt-BR')}</span>
                  )}
                </Space>
                {inv.message && (
                  <div style={{ fontSize: 13, color: '#555', marginTop: 6, fontStyle: 'italic' }}>
                    "{inv.message}"
                  </div>
                )}
              </div>
              <Space wrap>
                <Button icon={<MessageOutlined />} onClick={() => openChat(inv)}>
                  Conversar
                </Button>
                {inv.status === 'PENDING' && (
                  <>
                    <Button icon={<CloseOutlined />} danger loading={acting === inv.id}
                      onClick={() => respond(inv, 'reject')}>Recusar</Button>
                    <Button type="primary" icon={<CheckOutlined />} loading={acting === inv.id}
                      onClick={() => respond(inv, 'accept')}>Aceitar</Button>
                  </>
                )}
              </Space>
            </div>
          </Card>
        ))
      )}

      <Drawer
        title={chatInvite
          ? <span><MessageOutlined /> {chatInvite.player_name || 'Jogador'}</span>
          : 'Chat'}
        placement="right" width={480}
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        styles={{ body: { display: 'flex', flexDirection: 'column', padding: 0 } }}
      >
        <div style={{ flex: 1, overflowY: 'auto', padding: 16, background: '#f5f7fa' }}>
          {chatMsgs.length === 0 ? (
            <Empty description="Sem mensagens ainda. Envia a primeira!" />
          ) : (
            chatMsgs.map(m => {
              const isMine = m.sender_type === 'team';
              return (
                <div key={m.id} style={{
                  display: 'flex', justifyContent: isMine ? 'flex-end' : 'flex-start', marginBottom: 8,
                }}>
                  <div style={{
                    maxWidth: '78%',
                    background: isMine ? '#dcf8c6' : '#fff',
                    padding: '8px 12px', borderRadius: 12, fontSize: 13,
                    boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                  }}>
                    <div style={{ fontSize: 10, color: '#666', marginBottom: 2 }}>
                      {isMine ? '🛡 Você (time)' : '👤 Jogador'} · {new Date(m.created_at).toLocaleString('pt-BR')}
                    </div>
                    <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.body}</div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={chatBottomRef} />
        </div>
        <div style={{ padding: 12, borderTop: '1px solid #e5e8ec', background: '#fff' }}>
          <Space.Compact style={{ width: '100%' }}>
            <Input.TextArea
              value={chatDraft}
              onChange={e => setChatDraft(e.target.value)}
              placeholder="Escreve pro jogador…"
              autoSize={{ minRows: 1, maxRows: 4 }}
              onPressEnter={e => { if (!e.shiftKey) { e.preventDefault(); sendChat(); } }}
            />
            <Button type="primary" icon={<SendOutlined />} loading={chatSending}
              onClick={sendChat} disabled={!chatDraft.trim()}>
              Enviar
            </Button>
          </Space.Compact>
        </div>
      </Drawer>
    </div>
  );
}
